import { BCButton, BCCheckbox, BCExtremeGridEntities } from '@bimser/components';
import { MLHelper, compareMatchWithCollection, isNullOrUndefined, toUpperCase } from '@bimser/core';
import { GetIconComponent } from '@bimser/icons';
import IconAction from '@bimser/icons/16/action';
import GetIcon16ByName from '@bimser/icons/lib/getIcon16ByName';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import { onColumnButtonClick } from '..';
import DataGrid from '../..';
import { EContextMenuTargetTypes } from '../../../../../../entities';
import { EBatchFieldTypes } from '../../../../entities/EventArgs';
import { IControlProperties } from '../../../../entities/IControlProperties';
import Styles from '../../assets/DataGridComponentStyles.scss';
import { DataTypes, EditorTypes, ICellTemplateArgs, IControlDataGridColumn } from '../../entities';
import { getSelectColumn } from './getSelectColumnProperties';
import _get from 'lodash/get';

dayjs.extend(localeData);

async function getColumnProperties(
	this: DataGrid,
	column: IControlDataGridColumn<IControlProperties>,
	index: number
): Promise<BCExtremeGridEntities.IExtremeGridColumn> {
	if (column.editType == 'comboBox') {
		return getSelectColumn.call(this, column, index);
	}

	return {
		...getCommonDefaultProperties.call(this, column, index),
		tag: getColumnTag.call(this, column),
		editorOptions: getColumnEditorOptions.call(this, column),
		allowEditing: getColumnEditingEnabled(column),
		autoIncrement: column.autoIncrement,
		formatOptions: getColumnFormat(column),
		iconMatchers: column.iconMatchers,
		iconSourceField: column.iconSourceField,
		editorEvents: getEditorEvents.call(this, column),
		sourceType: column.sourceType,
		enlargeOnClick: column.enlargeOnClick,
		imageWidth: column.imageWidth,
		defaultImage: column.defaultImage,
		sizeUnit: column.sizeUnit,
		columnMenu: {
			enabled:
				this.contextMenuTarget === EContextMenuTargetTypes.Column &&
				this.contextMenuColumnKey === column.name
		}
	};
}

function getShowEditorProp(
	editingMode: string,
	column: IControlDataGridColumn<IControlProperties>
): boolean {
	let showEditorAlways: boolean = column.showEditorAlways;
	if (editingMode !== 'cell' && column.dbType?.type !== 'boolean') {
		showEditorAlways = false;
	}
	return showEditorAlways;
}

function getCommonDefaultProperties(
	this: DataGrid,
	column: IControlDataGridColumn<IControlProperties>,
	index: number
): BCExtremeGridEntities.IExtremeGridColumn {
	const showEditorProp: boolean = getShowEditorProp(this.editing.mode, column);

	return {
		name: column.name,
		caption: MLHelper.getMLText(column.caption) || toUpperCase(column.name),
		align: column.align,
		width: column.width,
		visible: getColumnVisible.call(this, column),
		dataType: getDataType(column),
		filtering: getColumnFiltering.call(this, column),
		sorting: getColumnSorting.call(this, column),
		editorType: EditorTypes[column.editType] as any,
		formatType: column?.formatType?.type,
		order: index,
		disabledToExport: column.disableToExport,
		allowHiding: column.allowHiding,
		allowNull:
			column.dbType.type === 'boolean' ||
			!column.visible ||
			column.autoIncrement ||
			column.allowNull,
		nullText: MLHelper.getMLText(column.nullText),
		customizeNullText: column.customizeNullText,
		checkUniqueValue: column.checkUniqueValue,
		showEditorAlways: showEditorProp,
		headerFilterEnabled: column.headerFilterEnabled || false,
		hidingPriority: column.hidingPriority,
		searchingEnabled:
			column?.formatType?.type == 'image' || column?.formatType?.type == 'actionButton'
				? false
				: true,
		cellTemplate: (props: ICellTemplateArgs) => getCellTemplate.call(this, props),
		setCellValue: this.setCellValueEvent
	};
}

function getEditorEvents(this: DataGrid, column: IControlDataGridColumn<IControlProperties>) {
	const args = { name: column.name, events: {} };
	this.emit('event:OnCellEditorInitilizing', args);
	return args.events || {};
}

function getColumnVisible(this: DataGrid, column: IControlDataGridColumn<IControlProperties>) {
	return column.visible;
}

function getDataType(column: IControlDataGridColumn<IControlProperties>) {
	let type = DataTypes[column?.dbType?.type as any];
	switch (column.editType) {
		case 'checkBox':
			type = 'boolean';
			break;
		case 'numberBox':
			type = 'number';
			break;
		case 'datePicker':
			type = 'date';
			break;
		case 'dateTimePicker':
			type = 'datetime';
			break;
		case 'dateRangePicker':
			type = 'dateRange';
			break;
		case 'timePicker':
			type = 'time';
			break;
		default:
			break;
	}

	return type;
}

const getActionButtonDisability = (
	readOnly: boolean,
	disabled: boolean,
	column: IControlDataGridColumn<IControlProperties>
): boolean => {
	const isButtonTypeOpen =
		column.actionButtonProps.typeName === 'OpenProcessArgs' ||
		column.actionButtonProps.typeName === 'OpenFormArgs';

	if (isButtonTypeOpen) {
		return disabled;
	}

	const isNotEditable =
		!isNullOrUndefined(column.actionButtonProps.args.editable) &&
		!column.actionButtonProps.args.editable;

	return isNotEditable ? false : readOnly || disabled;
};

function getColumnTag(this: DataGrid, column: IControlDataGridColumn<IControlProperties>) {
	let tag = null;

	switch (column?.dbType?.type) {
		case 'none': {
			if (column.actionButtonProps && column.editType === 'actionButton') {
				const Icon = GetIcon16ByName(column.actionButtonProps.icon, IconAction);
				tag = {
					typeName: column.actionButtonProps.typeName,
					onClick: args => onColumnButtonClick.call(this, args, column),
					title: MLHelper.getMLText(column.actionButtonProps.title),
					showType: 'custom',
					type: 'default',
					children: <Icon />,
					dataType: 'none',
					disabled: getActionButtonDisability(
						this.__isReadOnly(),
						this.__isDisabled(),
						column
					)
				};
			}
			break;
		}
		case 'object': {
			if (column.editType == 'multiLanguageTextBox') {
				tag = {
					currentLanguage: this.__form.currentLanguage,
					supportedLanguages: this.__form.supportedLanguages
				};
			}
			break;
		}
		default:
			break;
	}
	return tag;
}

export function getColumnFiltering(
	this: DataGrid,
	column: IControlDataGridColumn<IControlProperties>
) {
	let filtering: BCExtremeGridEntities.IExtremeGridColumn['filtering'] = null;

	const filter = this.__filtering.find(({ columnName }) => columnName == column.name);
	if (filter) {
		filtering = {
			enabled: true,
			value: filter.value,
			values: filter.values,
			selectedOperation: filter.operation,
			filterType: filter.filterType
		};
	}

	if (column?.editType == 'actionButton' || column?.formatType?.type == 'image') {
		filtering = {
			enabled: false
		};
	}

	return filtering;
}

function getColumnEditingEnabled(column: IControlDataGridColumn<IControlProperties>) {
	return column?.editType == 'actionButton'
		? false
		: column.editingEnabled && !column.autoIncrement;
}

function getColumnSorting(this: DataGrid, column: IControlDataGridColumn<IControlProperties>) {
	let sorting: BCExtremeGridEntities.IExtremeGridColumn['sorting'] = null;

	if (this.__sorting?.length) {
		const sortIndex = this.__sorting.findIndex(({ columnName }) => columnName == column.name);
		sorting =
			sortIndex > -1
				? { order: this.__sorting[sortIndex]?.direction, index: sortIndex }
				: undefined;
	}

	if (column?.editType == 'actionButton') {
		sorting = {
			enabled: false
		};
	}
	return sorting;
}

function getColumnEditorOptions(
	this: DataGrid,
	column: IControlDataGridColumn<IControlProperties>
) {
	let editorOptions: BCExtremeGridEntities.IExtremeGridColumn['editorOptions'] = {
		editType: column.editType
	};
	const control: any = column.editControl;

	switch (column?.editType) {
		case 'checkBox': {
			editorOptions = {
				value: column.defaultValue
			};
			break;
		}
		case 'textBox':
		case 'multiLanguageTextBox': {
			if (control?.maxLength) {
				editorOptions = {
					maxLength: control.maxLength
				};
			}
			break;
		}
		case 'timePicker':
		case 'datePicker':
		case 'dateTimePicker': {
			editorOptions = {
				allowClear: true
			};
			break;
		}
		case 'numberBox': {
			editorOptions = {
				precision: column.precision || 0,
				useThousandSeparator: column.useThousandSeparator || false
			};
			break;
		}
		default:
			break;
	}

	return editorOptions;
}

function getColumnFormat(column: IControlDataGridColumn<IControlProperties>) {
	const formatOptions = { format: null, formatType: column?.formatType?.type };

	switch (column?.editType) {
		case 'datePicker':
		case 'dateTimePicker': {
			formatOptions.format = column.format;
			if (isNullOrUndefined(column.format)) {
				formatOptions.format =
					column?.dbType?.type == 'dateTime'
						? dayjs().localeData().longDateFormat('L') +
						  ' ' +
						  dayjs().localeData().longDateFormat('LT')
						: dayjs().localeData().longDateFormat('L');
			}
			break;
		}
		case 'dateRangePicker': {
			formatOptions.format = column.format;
			break;
		}
		case 'timePicker': {
			formatOptions.format = column.format || 'HH:mm:ss';
			break;
		}
		default:
			break;
	}

	return formatOptions;
}

function getCellTemplate(this: DataGrid, props: ICellTemplateArgs) {
	if (
		this.__noRenderContentCellValidation &&
		this.__noRenderContentCellValidation(props.row.data, props.column.name)
	) {
		return '';
	}
	if (props.column.dataType === 'object' && props.column.editorType == 'SelectBox') {
		let value =
			props.column.tag.getValueType === 'keyTextValue'
				? props.value?.text || props.value?.value || null
				: props.value;

		const item = props.column.editorOptions?.options?.find(i => i.value == props.value?.value);
		if (item) {
			value = props.value?.text || item.text;
		}
		if (item?.icon) {
			return renderIcon(item.icon, value);
		}

		const cellValue = _get(props.row.data, props.column.iconSourceField);
		const icon = props.column.iconMatchers?.find(matcher =>
			compareMatchWithCollection({
				value: cellValue,
				compareValue: matcher.value,
				dataType: props.column.dataType,
				editType: props.column.editorType
			})
		)?.icon;

		if (icon) {
			const Icon = GetIcon16ByName(icon);
			return (
				<div className={Styles.CellWithIcon}>
					<Icon className={Styles.Icon} />
					<span
						className={Styles.selectCellTemplateStyle}
						title={value}
					>
						{value}
					</span>
				</div>
			);
		}

		return (
			<span
				className={Styles.selectCellTemplateStyle}
				title={value}
			>
				{value}
			</span>
		);
	} else if (props.column.dataType === 'none' && props.column?.editorType == 'Button') {
		const onClickButton = args => {
			const readOnlyTypes = ['OpenFormArgs', 'OpenProcessArgs', 'ExecuteCustomActionArgs'];
			if (this.__isReadOnly() && !readOnlyTypes.includes(props.column.tag.typeName)) {
				return;
			}
			args.event.stopPropagation();
			props.column.tag?.onClick?.({ ...args, row: props.row?.data });
		};
		const actionButtonEditable = this.columns.find(
			__columns => __columns.name === props.column.name
		)?.actionButtonProps?.args?.editable;
		const disabled: boolean =
			!isNullOrUndefined(actionButtonEditable) && !actionButtonEditable
				? false
				: this.__isDisabled();

		return (
			<BCButton
				{...props.column.tag}
				disabled={disabled}
				onClick={onClickButton}
			/>
		);
	} else if (
		props.column.dataType === 'boolean' &&
		this.__editableWithCellMode?.includes(props.column.name)
	) {
		const rowId = props.row.data[this.getUniqueDataField()];
		let value = this.batchOperations
			.find(i => i.uniqueFieldValue === rowId)
			?.fields?.find(x => x.columnName === props.column.name)?.value;
		value = isNullOrUndefined(value) ? props.value : value;
		const batchField = {
			columnName: props.column.name,
			value: !value,
			type: EBatchFieldTypes.Boolean
		};
		return (
			<BCCheckbox
				checked={value}
				onChange={(args: any) => {
					let newBatchOperations = [];
					if (this.batchOperations.find(i => i.uniqueFieldValue == rowId)) {
						newBatchOperations = [...this.batchOperations].map(x => {
							if (x.uniqueFieldValue == rowId) {
								x.fields = [
									...x.fields.filter(f => f.columnName != props.column.name),
									batchField
								];
							}
							return x;
						});
					} else {
						newBatchOperations = [
							...this.batchOperations,
							{ type: 'update', uniqueFieldValue: rowId, fields: [batchField] }
						];
					}
					this.onRowChangesChange(null, rowId, props.column.name, newBatchOperations);
				}}
			></BCCheckbox>
		);
	}

	if (props.column.iconMatchers?.length) {
		const cellValue = _get(props.row.data, props.column.iconSourceField);
		const iconSourceColumn = this.columns.find(
			col => col.name === props.column.iconSourceField.split('.').shift()
		);
		const icon = props.column.iconMatchers.find(matcher =>
			compareMatchWithCollection({
				value: cellValue,
				compareValue: matcher.value,
				dataType: props.column.dataType,
				format: props.column?.formatOptions?.format,
				editType: props.column.editorType
			})
		)?.icon;

		if (icon) {
			const Icon = GetIcon16ByName(icon);
			return (
				<div className={Styles.CellWithIcon}>
					<Icon className={Styles.Icon} />
					{props.displayText}
				</div>
			);
		}
	}
}

function renderIcon(icon: any, value: string) {
	if (icon) {
		return (
			<div className={Styles.CellWithIcon}>
				{GetIconComponent(icon, {
					className: Styles.Icon
				})}
				{value}
			</div>
		);
	}
}

export {
	getColumnEditingEnabled,
	getColumnProperties,
	getColumnSorting,
	getColumnVisible,
	getCommonDefaultProperties
};
